<template>
  <div class="search-content">
    <div v-if="isLoading" class="search-content__loading" />
    <div
      class="search-content__overlay"
      :class="{ 'is-open': isOpen }"
      @click="toggle_openSearch(false)"
    />

    <div class="search-content__container">
      <div class="search-content__wrap">
        <div class="search-content__scroller-wrap">
          <SearchHeadline
            class="search-content__headline"
            :title="headlineText.title"
            :text="headlineText.text"
          />

          <div
            ref="scrollerRef"
            class="search-content__scroller"
            @scroll="scrollerHandler"
          >
            <ViewResult
              v-if="isShowResult"
              key="ViewResult"
              :search-value="searchValue"
              :list="data[ENUM_CATEGORY.TOP.TERM]"
              @onMountResult="scrollerHandler"
              @onNotFoundResult="setNotFoundResultStatus"
            />

            <ViewGuest
              v-else
              :list="data[ENUM_CATEGORY.NEW.TERM]"
            />

            <div v-if="isShowAdditionalBlock" class="search-content__additional">
              <Transition name="fade">
                <UiBtn
                  v-if="isShowToTopButton"
                  class="search-content__top-button"
                  color="default"
                  :text="$tc('lobby.search.toUp')"
                  icon="chevrons/chevron-up"
                  icon-right
                  @click="scrollerScrollToTop"
                />
              </Transition>

              <RegistrationInfo v-if="!isLoggedIn" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ViewResult from '@/components/Lobby/SearchModal/ViewResult'
import ViewGuest from '@/components/Lobby/SearchModal/ViewGuest'
import SearchHeadline from '@/components/Lobby/SearchModal/components/SearchHeadline'

import { ENUM_CATEGORY } from '@/components/Lobby/enum.js'
import { addScrollableSelector, disablePageScroll, enablePageScroll } from 'scroll-lock'

export default {
  name: 'SearchModalContent',
  components: {
    ViewResult,
    ViewGuest,
    SearchHeadline,
    RegistrationInfo: () => import('@/components/Lobby/SearchModal/components/RegistrationInfo'),
  },
  props: {
    isOpen: { type: Boolean },
    searchValue: { type: String },
  },
  data() {
    return {
      data: {
        [ENUM_CATEGORY.TOP.TERM]: null,
        [ENUM_CATEGORY.NEW.TERM]: null,
      },
      ENUM_CATEGORY,
      isLoading: false,
      isShowProviderTitle: false,
      isNotFoundResult: false,
      isShowToTopButton: false,
      scrollTicking: false,
    }
  },
  computed: {
    ...mapGetters('user', ['isLoggedIn']),
    isShowResult() {
      return this.searchValue.length > 2
    },
    headlineText() {
      if (this.isShowResult) {
        return {
          title: this.isShowProviderTitle ? this.$t('lobby.search.titleProviders') : this.$t('lobby.search.titleGames'),
          text: this.$t('lobby.search.textResult', { value: this.searchValue }),
        }
      }
      return {
        title: this.$t('lobby.search.titleGames'),
        text: this.$t('lobby.search.textDefault'),
      }
    },
    isShowAdditionalBlock() {
      return !(this.isShowResult && this.isNotFoundResult)
    },
  },
  watch: {
    searchValue(v) {
      if (!v) this.result = null
    },
    isShowResult(v) {
      if (!v) this.scrollerHandler()
    },
  },
  beforeMount() {
    this.fetchData()
  },
  mounted() {
    this.$nextTick(() => {
      disablePageScroll()
      addScrollableSelector('.search-content__scroller')
    })
    this.setScrollEventToScroller()
  },
  beforeDestroy() {
    enablePageScroll()
    this.removeScrollEventFromScroller()
  },
  methods: {
    ...mapActions('lobby', ['toggle_openSearch']),
    async fetchData() {
      this.isLoading = true

      const promises = [
        this.fetchGames(ENUM_CATEGORY.TOP.TERM),
        this.fetchGames(ENUM_CATEGORY.NEW.TERM, 30),
      ]
      await Promise.allSettled(promises)

      this.isLoading = false
    },
    async fetchGames(term, take) {
      try {
        const { data } = await this.$api._games.getSubsectionItem({ term, take })

        this.data[term] = data.games.result
      }
      catch (error) {
        console.log(error)
      }
    },
    scrollerHandler() {
      const element = this.$refs.scrollerRef
      const headingElem = element?.querySelector('[data-headline]')

      if (!headingElem) {
        this.isShowProviderTitle = false
        return
      }
      const elementRect = headingElem.getBoundingClientRect()
      const offsetParent = element.offsetTop
      this.isShowProviderTitle = (elementRect.height + elementRect.y) - offsetParent > 40
    },
    setNotFoundResultStatus(v) {
      this.isNotFoundResult = v
    },
    onScrollerScroll() {
      if (!this.scrollTicking) {
        this.scrollTicking = true
        requestAnimationFrame(() => {
          this.showToTopButton()
          this.scrollTicking = false
        })
      }
    },
    setScrollEventToScroller() {
      this.$refs?.scrollerRef?.addEventListener('scroll', this.onScrollerScroll)
    },
    removeScrollEventFromScroller() {
      this.$refs?.scrollerRef?.removeEventListener('scroll', this.onScrollerScroll)
    },
    showToTopButton() {
      const element = this.$refs?.scrollerRef
      const elementScrollTop = element?.scrollTop

      if (elementScrollTop && elementScrollTop > 300) {
        this.isShowToTopButton = true
      }
      else {
        this.isShowToTopButton = false
      }
    },
    scrollerScrollToTop() {
      this.$refs?.scrollerRef?.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      })
    },
  },
}
</script>

<style lang="scss" src="./style.scss" scoped></style>
